// TooltipWrapper.tsx
// A wrapper component that displays a tooltip when hovering over its children.
// Intended for use with ShadCN Tooltip components and Storybook documentation.

import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@bnsn/ui/components/tooltip'
import { type ReactNode } from 'react'

/**
 * Props for TooltipWrapper component.
 *
 * @property tooltipText - The text to display inside the tooltip. If not provided, no tooltip is shown.
 * @property children - The content that will trigger the tooltip on hover or focus.
 * @property className - Optional className for the wrapper span.
 * @property side - The preferred side for the tooltip to appear ('left', 'right', 'top', 'bottom'). Defaults to 'top'.
 */
interface TooltipWrapperProps {
  tooltipText?: string | null
  children: ReactNode
  className?: string
  side?: 'left' | 'right' | 'top' | 'bottom'
}

/**
 * TooltipWrapper displays a tooltip when hovering or focusing on its children.
 *
 * If no tooltipText is provided, it simply renders the children without a tooltip.
 *
 * @example
 * <TooltipWrapper tooltipText="This is a tooltip">Hover me</TooltipWrapper>
 */
export const TooltipWrapper: React.FC<TooltipWrapperProps> = ({ tooltipText, children, className, side = 'top' }) => {
  if (!tooltipText) {
    return <>{children}</>
  }
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger aria-label={tooltipText} asChild>
          <span className={className} role="tooltip">
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-md whitespace-pre-wrap border-none">
          {tooltipText}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
